// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CapsuleButton } = require('client-v2/common/components/buttons')
const Modal = require('client-v2/common/components/modal')
const ToggleButton = require('client-v2/common/components/toggle-button')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const CollectionEditModalHeaderImagePreview = require('./components/header-image-preview')
const CollectionEditModalImagePreview = require('./components/image-preview')

const bem = BemEntity('CollectionEditModal')

function CollectionEditModal({
  className,
  // Header image props
  isHeaderImageUploadPending,
  headerImage,
  onHeaderImageEdit,
  // Cover image props
  coverImage,
  onCoverImageEdit,
  // Form props
  ...props
}) {
  // Hooks
  const onHeaderImageClick = React.useCallback(
    () => {
      onHeaderImageEdit({ savedImageUrl: headerImage })
    },
    [ onHeaderImageEdit, headerImage ]
  )
  const onHeaderImageDrop = React.useCallback(
    ({ file }) => {
      onHeaderImageEdit({ droppedFile: file })
    },
    [ onHeaderImageEdit ]
  )
  const onCoverImageClick = React.useCallback(
    () => {
      onCoverImageEdit()
    },
    [ onCoverImageEdit ]
  )
  // Render
  return (
    tag(Modal, {
      className: classNames(
        className,
        bem.block()
      ),
      ariaLabel: `Edit collection`,
      onDismiss: props.onDismiss
    },
      tag('div', {
        className: bem.element('content')
      },
        tag('div', {
          className: bem.element('header')
        },
          'Edit Collection'
        ),

        tag(CollectionEditModalHeaderImagePreview, {
          className: bem.element('header-image'),
          pending: isHeaderImageUploadPending,
          value: headerImage,
          onClick: onHeaderImageClick,
          onDrop: onHeaderImageDrop
        }),

        tag(CollectionEditModalImagePreview, {
          className: bem.element('cover-image'),
          value: coverImage,
          label: 'Edit Cover Image',
          onClick: onCoverImageClick
        }),

        tag(CollectionEditModalForm, props)
      )
    )
  )
}

module.exports = CollectionEditModal

// BEM Block entity Element components

function CollectionEditModalForm({
  // Flags
  isCollectionDeletePending,
  isCollectionUpdatePending,
  // Values
  collection,
  // Callbacks
  onCollectionDelete,
  onCollectionSubmit
}) {
  // State hooks
  const [ name, setName ] = React.useState(collection.name)
  const [ description, setDescription ] = React.useState(collection.description)
  const [ visibility, setVisibility ] = React.useState(collection.visibility)
  // Callback hooks
  const onNameChange = React.useCallback(
    event => {
      setName(event.target.value)
    },
    [ setName ]
  )
  const onDescriptionChange = React.useCallback(
    event => {
      setDescription(event.target.value)
    },
    [ setDescription ]
  )
  const onVisibilityChange = React.useCallback(
    () => {
      setVisibility(visibility === 'private' ? 'public' : 'private')
    },
    [ setVisibility, visibility ]
  )
  const onSubmit = React.useCallback(
    event => {
      event.preventDefault()
      onCollectionSubmit({ name, description, visibility })
    },
    [ onCollectionSubmit, name, description, visibility ]
  )
  // Render
  return (
    tag('form', {
      className: bem.element('form'),
      onSubmit
    },
      tag('label', {
        className: bem.element('label')
      },
        'Title'
      ),
      tag('input', {
        className: bem.element('input'),
        value: name,
        onChange: onNameChange
      }),
      tag('label', {
        className: bem.element('label')
      },
        'Description'
      ),
      tag('textarea', {
        className: bem.element('textarea'),
        maxLength: 150,
        value: description,
        onChange: onDescriptionChange
      }),
      tag('label', {
        className: bem.element('label')
      },
        'Privacy'
      ),
      tag('div', {
        className: bem.element('toggle'),
        onClick: onVisibilityChange
      },
        tag('div', {
          className: bem.element('toggle-title')
        },
          'Make this Collection private?'
        ),
        tag(ToggleButton, {
          className: bem.element('toggle-button'),
          checked: visibility === 'private',
          readOnly: true
        })
      ),
      tag('div', {
        className: bem.element('buttons')
      },
        tag(CapsuleButton, {
          className: bem.element('delete-button'),
          disabled: isCollectionDeletePending || isCollectionUpdatePending,
          onClick: onCollectionDelete
        },
          isCollectionDeletePending
          ? 'Deleting...'
          : 'Delete this Collection'
        ),
        tag(CapsuleButton, {
          className: bem.element('submit-button'),
          disabled: isCollectionDeletePending || isCollectionUpdatePending,
          type: 'submit'
        },
          isCollectionUpdatePending
          ? 'Saving...'
          : 'Save Changes'
        )
      )
    )
  )
}
